import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { apiErrorText } from '@/lib/api-error'
import { cardTitle, formCardSm, inlineInput } from '@/lib/form-styles'
import { FeldStatus, useFeld, useGruppe, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { fetchPositionPhaseSlots, fetchUsers, savePositionPhaseSlots, type PhaseSlotsAntwort } from '../api/projects-head.api'
import { updatePosition } from '../api/projects-phase-d.api'
import { slotAusAntwort, slotAusBasis, slotDaten, slotWerte } from '../lib/phasen-slot'
import { alsTag } from '../project-labels'
import type { PositionDetail } from '../types/phase-d.types'
import type { InheritedSlot, LookupUser, ProjectDetail, ProjectPhase } from '../types/project.types'
import { RollenBenutzer } from './auswahl'
import { TermineKarte } from './PositionDetailsTab'

/**
 * AP-35 Nachtrag 3 (Auftraggeber 19.9.2026) — **Reiter „Phasen & Zuständige" an
 * der Position.** Wie am Projekt, nur ohne Teilnehmer: je Termin (Phase) von/bis,
 * fixiert, Uhrzeit, Anmerkung — dazu die frühere Karte „Termine und
 * Fortschritt" aus den Details.
 *
 * Die Zuständigen stehen wie am Projekt im Block „Rollen" (Auftraggeber 19.9.2026),
 * nicht als Spalte der Tabelle.
 *
 * Vererbung (Blatt 3 3.1): Ohne eigenen Eintrag gilt der Termin des Projekts und
 * steht gedämpft da. „eigen" übernimmt ihn als Ausgangswert und macht die Zeile
 * bearbeitbar; abgehakt geht sie zurück auf das Projekt (der Server löscht den
 * leeren Slot der Position). Ein Slot gilt immer als Ganzes — halbe Abweichungen
 * gibt es nicht.
 *
 * Seit AP-36 F speichert der Reiter **feldweise ohne Knopf**: Jede Zeile ist eine
 * Gruppe und schreibt beim Verlassen ihren eigenen Slot. Der Haken „eigen" bleibt
 * eine Handlung für sich — er legt die Abweichung an oder gibt sie zurück.
 */
export function PositionTermineTab({
  projekt,
  position,
  darfPflegen,
  offen,
  onChanged,
}: {
  projekt: ProjectDetail
  position: PositionDetail
  darfPflegen: boolean
  offen: boolean
  onChanged: () => Promise<void> | void
}) {
  const [daten, setDaten] = useState<PhaseSlotsAntwort | null>(null)
  const [benutzer, setBenutzer] = useState<LookupUser[]>([])
  const [fehler, setFehler] = useState<string | null>(null)

  const laden = useCallback(async () => {
    try {
      setDaten(await fetchPositionPhaseSlots(projekt.id, position.id))
      setFehler(null)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }, [projekt.id, position.id])

  useEffect(() => {
    void laden()
    void fetchUsers().then(setBenutzer).catch(() => undefined)
  }, [laden])

  const slots = useMemo(() => new Map((daten?.slots ?? []).map((s) => [s.phaseId, s])), [daten])
  const bearbeitbar = darfPflegen && offen

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
      <div className={formCardSm}>
        <h3 className={cardTitle}>Phasen</h3>
        <p className="mt-1 text-xs text-muted-foreground">Ohne Haken „eigen“ gilt die Phase des Projekts.</p>
        {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-3">Phase</th>
                <th className="py-2 pr-3">eigen</th>
                <th className="py-2 pr-3">von</th>
                <th className="py-2 pr-3">bis</th>
                <th className="py-2 pr-3">fixiert</th>
                <th className="py-2 pr-3">Uhrzeit</th>
                <th className="py-2 pr-3">Anmerkung</th>
              </tr>
            </thead>
            <tbody>
              {(daten?.phases ?? []).map((phase) => {
                const slot = slots.get(phase.id)
                if (!slot) return null
                return (
                  <TerminZeile
                    key={phase.id}
                    projektId={projekt.id}
                    positionId={position.id}
                    phase={phase}
                    slot={slot}
                    bearbeitbar={bearbeitbar}
                    onAntwort={setDaten}
                    onFehler={setFehler}
                  />
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-4">
        <TermineKarte
          position={position}
          offen={bearbeitbar}
          titel="Liefertermin und Fortschritt"
          speichere={async (d) => {
            if (Object.keys(d).length === 0) return true
            await updatePosition(projekt.id, position.id, d)
            await onChanged()
            return true
          }}
        />

        <div className={formCardSm}>
          <h3 className={cardTitle}>Rollen</h3>
          <p className="mt-1 text-xs text-muted-foreground">Grau = wie im Projekt.</p>
          <div className="mt-3 max-w-sm space-y-1.5">
            {(daten?.phases ?? []).map((phase) => {
              const slot = slots.get(phase.id)
              if (!slot) return null
              return (
                <TerminZustaendig
                  key={phase.id}
                  projektId={projekt.id}
                  positionId={position.id}
                  phase={phase}
                  slot={slot}
                  benutzer={benutzer}
                  bearbeitbar={bearbeitbar}
                  onAntwort={setDaten}
                  onFehler={setFehler}
                />
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Die Speicherstelle eines Positions-Slots. Solange die Position erbt, gibt es
 * keinen eigenen Satz — dann steht der Wert des Projekts in der Anzeige, aber
 * **kein Stand**, denn geschrieben wird erst, wenn „eigen" gesetzt ist.
 */
function usePositionsSlot(
  projektId: string,
  positionId: string,
  phase: ProjectPhase,
  slot: InheritedSlot,
  onAntwort: (a: PhaseSlotsAntwort) => void,
) {
  const eigen = !slot.inherited && !!slot.own
  const quelle = eigen ? slot.own : slot.effective
  const daten = useMemo(() => slotDaten(quelle), [quelle])
  // Wie am Projekt: Der ganze Slot geht hinaus, die Werte dafür kommen aus der
  // Basis der Stelle — nach einem 409 sind das die Werte des Servers.
  const halter = useRef<Speicherstelle<unknown> | null>(null)
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `phaseslot:${projektId}:${positionId}:${phase.id}`,
    bezeichnung: `Phase ${phase.name}`,
    daten,
    schreibe: (patch, stand) =>
      savePositionPhaseSlots(
        projektId,
        positionId,
        [{ ...slotAusBasis(halter.current?.daten ?? daten, patch), phaseId: phase.id }],
        stand,
      ).then((a) => {
        onAntwort(a)
        return a as unknown as Datensatz
      }),
    // Maßgeblich ist der **eigene** Slot der Position; erbt sie noch, hat sie
    // keinen Stand — geschrieben wird ohnehin erst mit „eigen".
    werte: (a) => slotAusAntwort(a, phase.id, true),
    stand: (a) => slotAusAntwort(a, phase.id, true).updatedAt as string | undefined,
  }) as Speicherstelle<unknown>
  halter.current = stelle
  return stelle
}

/** Eine Phase an der Position: „eigen" plus Zeitraum, Fixierung und Anmerkung. */
function TerminZeile({
  projektId,
  positionId,
  phase,
  slot,
  bearbeitbar,
  onAntwort,
  onFehler,
}: {
  projektId: string
  positionId: string
  phase: ProjectPhase
  slot: InheritedSlot
  bearbeitbar: boolean
  onAntwort: (a: PhaseSlotsAntwort) => void
  onFehler: (t: string | null) => void
}) {
  const eigen = !slot.inherited && !!slot.own
  const aktiv = bearbeitbar && eigen
  const gedaempft = eigen ? '' : 'text-muted-foreground'
  const stelle = usePositionsSlot(projektId, positionId, phase, slot, onAntwort)
  const gruppe = useGruppe(stelle, {
    label: phase.name,
    pruefe: (w) => {
      const von = w.startsAt ? String(w.startsAt).slice(0, 10) : ''
      const bis = w.endsAt ? String(w.endsAt).slice(0, 10) : ''
      return von && bis && bis < von ? 'bis liegt vor von' : null
    },
  })
  const von = useFeld(stelle, 'startsAt', { art: 'datum', label: 'von', gruppe, gesperrt: !aktiv })
  const bis = useFeld(stelle, 'endsAt', { art: 'datum', label: 'bis', gruppe, gesperrt: !aktiv })
  const fixiert = useFeld<boolean>(stelle, 'isFixed', {
    art: 'schalter',
    label: 'fixiert',
    gruppe,
    gesperrt: !aktiv,
    // Ohne Fixierung ergibt eine Uhrzeit keinen Sinn — der Server weist sie zurück.
    folge: (w) => (w ? {} : { fixedTime: '' }),
  })
  const uhrzeit = useFeld(stelle, 'fixedTime', { label: 'Uhrzeit', gruppe, gesperrt: !aktiv || !fixiert.wert })
  const notiz = useFeld(stelle, 'note', { label: 'Anmerkung', gruppe, gesperrt: !aktiv })

  /**
   * „eigen" ist kein Feld des Satzes, sondern legt ihn an oder gibt ihn zurück:
   * angehakt übernimmt die Position die Werte des Projekts als Ausgangswert
   * (Anlegen, also ohne Stand), abgehakt löscht der Server den leeren Slot.
   */
  async function eigenSetzen(an: boolean) {
    try {
      onFehler(null)
      const eintrag = an ? { phaseId: phase.id, ...slotWerte(slot.effective) } : { phaseId: phase.id }
      onAntwort(await savePositionPhaseSlots(projektId, positionId, [eintrag], an ? undefined : slot.own?.updatedAt))
    } catch (e) {
      onFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  return (
    <tr
      className="border-b border-border/60"
      onFocus={gruppe.onFocus}
      onBlur={gruppe.onBlur}
      onKeyDown={(e) => {
        if (e.key === 'Escape') gruppe.verwerfen()
      }}
    >
      <td className="py-2 pr-3">
        <span className="inline-flex items-center gap-2">
          {phase.color && <span className="h-2.5 w-2.5 rounded-full" style={{ background: phase.color }} />}
          {phase.name}
          <FeldStatus status={gruppe} />
        </span>
      </td>
      <td className="py-2 pr-3">
        <input
          type="checkbox"
          disabled={!bearbeitbar}
          checked={eigen}
          aria-label={`${phase.name}: eigener Termin`}
          onChange={(e) => void eigenSetzen(e.target.checked)}
        />
      </td>
      {aktiv ? (
        <>
          <td className="py-2 pr-3">
            <input type="date" className={inlineInput} {...von.props} />
          </td>
          <td className="py-2 pr-3">
            <input type="date" className={inlineInput} {...bis.props} />
          </td>
          <td className="py-2 pr-3">
            <input type="checkbox" {...fixiert.schalter} />
          </td>
          <td className="py-2 pr-3">
            <input type="time" className={inlineInput} {...uhrzeit.props} />
          </td>
          <td className="py-2 pr-3">
            <input className={inlineInput} {...notiz.props} />
          </td>
        </>
      ) : (
        <>
          <td className={`py-2 pr-3 ${gedaempft}`}>{alsTag(von.props.value || null)}</td>
          <td className={`py-2 pr-3 ${gedaempft}`}>{alsTag(bis.props.value || null)}</td>
          <td className={`py-2 pr-3 ${gedaempft}`}>{fixiert.wert ? 'ja' : '—'}</td>
          <td className={`py-2 pr-3 ${gedaempft}`}>{uhrzeit.props.value || '—'}</td>
          <td className={`py-2 pr-3 ${gedaempft}`}>{notiz.props.value || '—'}</td>
        </>
      )}
    </tr>
  )
}

/**
 * Der Zuständige einer Phase an der Position — dieselbe Stelle wie die Zeile.
 * Eine Rolle zu ändern macht den Termin zum eigenen der Position (Ausgangswert:
 * der des Projekts).
 */
function TerminZustaendig({
  projektId,
  positionId,
  phase,
  slot,
  benutzer,
  bearbeitbar,
  onAntwort,
  onFehler,
}: {
  projektId: string
  positionId: string
  phase: ProjectPhase
  slot: InheritedSlot
  benutzer: LookupUser[]
  bearbeitbar: boolean
  onAntwort: (a: PhaseSlotsAntwort) => void
  onFehler: (t: string | null) => void
}) {
  const eigen = !slot.inherited && !!slot.own
  const stelle = usePositionsSlot(projektId, positionId, phase, slot, onAntwort)
  const feld = useFeld<string | null>(stelle, 'responsibleUserId', { art: 'auswahl', label: phase.name, gesperrt: !bearbeitbar })

  /** Erbt die Position noch, entsteht mit der Rolle zuerst der eigene Slot. */
  async function setzen(userId: string) {
    if (eigen) {
      feld.setze(userId || null)
      return
    }
    try {
      onFehler(null)
      onAntwort(
        await savePositionPhaseSlots(projektId, positionId, [
          { phaseId: phase.id, ...slotWerte(slot.effective), responsibleUserId: userId },
        ]),
      )
    } catch (e) {
      onFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="w-28 shrink-0 truncate text-muted-foreground">{phase.name}</span>
      {bearbeitbar ? (
        <>
          <RollenBenutzer
            className={`${inlineInput} w-44 ${eigen ? '' : 'text-muted-foreground'}`}
            benutzer={benutzer}
            rolle={phase.key}
            leer="—"
            wert={feld.props.value}
            onChange={(v) => void setzen(v)}
          />
          <FeldStatus status={feld} />
        </>
      ) : (
        <span className={eigen ? '' : 'text-muted-foreground'}>{benutzer.find((b) => b.id === feld.props.value)?.name ?? '—'}</span>
      )}
    </label>
  )
}
